/* ------------------------------------------------------------------
   Predictive search - Solutions Bureaupak, 2026-07-30

   Attaches an as-you-type dropdown to every storefront search input.

   Why: Search & Discovery synonym groups are applied by Shopify's
   PREDICTIVE search engine only, never by the Liquid `search.results`
   object that renders /search. Before this file existed the theme never
   called the predictive endpoint, so all 81 synonym groups were dead
   weight. This is what makes them reachable.

   Also exposes window.SBPPredictive.rescue() - used by the search page
   to retry a zero-result query through the synonym-aware endpoint.

   No dependencies. Safe to delete: the forms fall back to plain submit.
------------------------------------------------------------------- */
(function () {
  'use strict';

  var CFG = window.SBP_PSEARCH || {};
  var ENDPOINT = CFG.url || '/search/suggest';
  var SECTION = 'predictive-search';
  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 180;
  var LIMIT = 6;

  var cache = Object.create(null);

  function buildUrl(term, limit) {
    return (
      ENDPOINT +
      '?q=' + encodeURIComponent(term) +
      '&resources[type]=product' +
      '&resources[limit]=' + (limit || LIMIT) +
      '&resources[options][unavailable_products]=last' +
      '&section_id=' + SECTION
    );
  }

  function fetchPanel(term, limit, signal) {
    var key = term + '|' + (limit || LIMIT);
    if (cache[key]) return Promise.resolve(cache[key]);
    return fetch(buildUrl(term, limit), { signal: signal })
      .then(function (r) {
        if (!r.ok) throw new Error('predictive search ' + r.status);
        return r.text();
      })
      .then(function (html) {
        cache[key] = html;
        return html;
      });
  }

  /* ---------------------------------------------------------- dropdown */

  function Dropdown(input) {
    this.input = input;
    this.form = input.closest('form');
    if (!this.form) return;

    this.form.classList.add('psearch-form');

    this.panel = document.createElement('div');
    this.panel.className = 'psearch-panel';
    this.panel.hidden = true;
    this.form.appendChild(this.panel);

    this.activeIndex = -1;
    this.controller = null;
    this.timer = null;

    input.setAttribute('autocomplete', 'off');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');

    this.onInput = this.onInput.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onFocus = this.onFocus.bind(this);

    input.addEventListener('input', this.onInput);
    input.addEventListener('keydown', this.onKeydown);
    input.addEventListener('focus', this.onFocus);

    var self = this;
    document.addEventListener('click', function (e) {
      if (!self.form.contains(e.target)) self.close();
    });
  }

  Dropdown.prototype.items = function () {
    return Array.prototype.slice.call(this.panel.querySelectorAll('.psearch-item'));
  };

  Dropdown.prototype.open = function () {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  };

  Dropdown.prototype.close = function () {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.setActive(-1);
  };

  Dropdown.prototype.setActive = function (i) {
    var items = this.items();
    items.forEach(function (el) {
      el.classList.remove('is-active');
      el.setAttribute('aria-selected', 'false');
    });
    this.activeIndex = i;
    if (i >= 0 && items[i]) {
      items[i].classList.add('is-active');
      items[i].setAttribute('aria-selected', 'true');
      if (items[i].scrollIntoView) items[i].scrollIntoView({ block: 'nearest' });
    }
  };

  Dropdown.prototype.onFocus = function () {
    if (this.panel.innerHTML.trim() && this.input.value.trim().length >= MIN_CHARS) this.open();
  };

  Dropdown.prototype.onInput = function () {
    var self = this;
    var term = this.input.value.trim();

    clearTimeout(this.timer);
    if (this.controller) this.controller.abort();

    if (term.length < MIN_CHARS) {
      this.close();
      this.panel.innerHTML = '';
      return;
    }

    this.timer = setTimeout(function () {
      self.controller = typeof AbortController === 'function' ? new AbortController() : null;
      fetchPanel(term, LIMIT, self.controller ? self.controller.signal : undefined)
        .then(function (html) {
          if (self.input.value.trim() !== term) return;
          self.panel.innerHTML = html;
          self.setActive(-1);
          self.open();
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          self.close();
        });
    }, DEBOUNCE_MS);
  };

  Dropdown.prototype.onKeydown = function (e) {
    var items = this.items();

    if (e.key === 'Escape') {
      this.close();
      return;
    }
    if (!items.length || this.panel.hidden) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.setActive((this.activeIndex + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.setActive(this.activeIndex <= 0 ? items.length - 1 : this.activeIndex - 1);
    } else if (e.key === 'Enter') {
      if (this.activeIndex >= 0 && items[this.activeIndex]) {
        var link = items[this.activeIndex].querySelector('a');
        if (link) {
          e.preventDefault();
          window.location.href = link.href;
        }
      }
    }
  };

  /* ------------------------------------------------- zero-result rescue */

  function rescue(container) {
    var term = container.getAttribute('data-term');
    if (!term) return;

    container.classList.add('psearch-rescue--loading');

    fetchPanel(term, 10)
      .then(function (html) {
        container.classList.remove('psearch-rescue--loading');
        var probe = document.createElement('div');
        probe.innerHTML = html;
        var inner = probe.querySelector('.psearch-inner');
        var count = inner ? parseInt(inner.getAttribute('data-count'), 10) : 0;
        if (!count) return;

        container.innerHTML = html;
        container.hidden = false;

        var empty = document.querySelector('.search-page .empty-state');
        if (empty) empty.hidden = true;

        /* The "0 results" line is the paragraph immediately above this block.
           Leaving it up while showing ten products reads as a bug. */
        var countLine = container.previousElementSibling;
        if (countLine && countLine.tagName === 'P') countLine.hidden = true;

        var heading = container.getAttribute('data-heading');
        if (heading) {
          var h = document.createElement('h2');
          h.className = 'psearch-rescue-heading';
          h.textContent = heading;
          container.insertBefore(h, container.firstChild);
        }
      })
      .catch(function () {
        container.classList.remove('psearch-rescue--loading');
      });
  }

  /* ----------------------------------------------------------- bootstrap */

  function init() {
    var inputs = document.querySelectorAll(
      '.header-search form input[name="q"], .mobile-nav-search form input[name="q"]'
    );
    Array.prototype.forEach.call(inputs, function (input) {
      if (input.dataset.psearchBound) return;
      input.dataset.psearchBound = '1';
      new Dropdown(input);
    });

    var box = document.querySelector('[data-psearch-rescue]');
    if (box) rescue(box);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.SBPPredictive = { init: init, rescue: rescue, fetchPanel: fetchPanel };
})();
